import * as React from "react";
import { useRef, useState, useCallback } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";

import { cn } from "@/lib/utils";

function Sheet({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot="sheet" {...props} />;
}

function SheetTrigger({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="sheet-trigger" {...props} />;
}

function SheetPortal({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="sheet-portal" {...props} />;
}

function SheetClose({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="sheet-close" {...props} />;
}

function SheetOverlay({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      data-slot="sheet-overlay"
      className={cn(
        "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50",
        className
      )}
      {...props}
    />
  );
}

type SheetContentProps = React.ComponentProps<typeof DialogPrimitive.Content> & {
  side?: "left" | "right" | "top" | "bottom";
  onSwipeClose?: () => void;
};

function SheetContent({
  className,
  children,
  side = "right",
  onSwipeClose,
  ...props
}: SheetContentProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  const [dragY, setDragY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const startY = useRef(0);
  const startDragY = useRef(0);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (side !== "bottom") return;
    startY.current = e.touches[0].clientY;
    startDragY.current = dragY;
    setIsDragging(true);
  }, [side, dragY]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (side !== "bottom" || !isDragging) return;
    const currentY = e.touches[0].clientY;
    const diff = currentY - startY.current;
    // Only allow dragging down (positive diff)
    if (diff > 0) {
      setDragY(diff);
    }
  }, [side, isDragging]);

  const handleTouchEnd = useCallback(() => {
    if (side !== "bottom") return;
    setIsDragging(false);
    
    // If dragged more than 100px, close the sheet
    if (dragY > 100) {
      onSwipeClose?.();
    }
    setDragY(0);
  }, [side, dragY, onSwipeClose]);

  const isBottomSheet = side === "bottom";

  return (
    <SheetPortal>
      <SheetOverlay />
      <DialogPrimitive.Content
        ref={contentRef}
        data-slot="sheet-content"
        className={cn(
          "bg-background fixed z-50 flex flex-col shadow-xl",
          !isDragging && "duration-300",
          "data-[state=open]:animate-in data-[state=closed]:animate-out",
          // Horizontal sides
          (side === "left" || side === "right") && "h-full w-[320px]",
          side === "right" && "inset-y-0 right-0 border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right",
          side === "left" && "inset-y-0 left-0 border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left",
          // Vertical sides
          (side === "top" || side === "bottom") && "w-full",
          side === "bottom" && "inset-x-0 bottom-0 h-[90vh] rounded-t-xl border-t data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
          side === "top" && "inset-x-0 top-0 border-b data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top",
          className
        )}
        style={isBottomSheet ? { transform: `translateY(${dragY}px)` } : undefined}
        onTouchStart={isBottomSheet ? handleTouchStart : undefined}
        onTouchMove={isBottomSheet ? handleTouchMove : undefined}
        onTouchEnd={isBottomSheet ? handleTouchEnd : undefined}
        {...props}
      >
        {/* Drag handle for bottom sheet */}
        {isBottomSheet && (
          <div className="flex justify-center py-2 cursor-grab active:cursor-grabbing">
            <div className="w-10 h-1 rounded-full bg-muted-foreground/30" />
          </div>
        )}
        {children}
      </DialogPrimitive.Content>
    </SheetPortal>
  );
}

function SheetHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sheet-header"
      className={cn(
        "flex items-center justify-between px-4 py-3 border-b",
        className
      )}
      {...props}
    />
  );
}

function SheetTitle({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="sheet-title"
      className={cn("text-sm font-semibold", className)}
      {...props}
    />
  );
}

function SheetDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="sheet-description"
      className={cn("text-muted-foreground text-sm", className)}
      {...props}
    />
  );
}

export {
  Sheet,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetOverlay,
  SheetPortal,
  SheetTitle,
  SheetTrigger,
  SheetDescription,
};
